import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { getModulesFromDatabase, UpdateModuleAdmin } from "../database/modules";
import { GetOutputsFromDatabase, createItem, updateItem, updateTranslation } from "../database/items";
import { getWebCatsFromDatabase, updateWebCat, getOffCatsFromDatabase, updateOffCat } from "../database/webcats";

type IoType = "ingangen" | "uitgangen";
type LanguageCode = "NL" | "FR" | "EN";
type AdminSection = "modules" | "ingangen" | "uitgangen" | "webcats" | "offcats";

interface AdminModule {
  id: number;
  type: string;
  name: string;
  busConsumption: number;
  outputs: number;
  adresses: number;
  prijs: number;
  obsolete: number;
  webCat: number;
  offCat: number;
}

interface AdminItem {
  id: number;
  nodeid: string;
  type: string;
  typeIcon: string;
  moduleType: string;
  webCat: number;
  offCat: number;
  label: string;
  labels: Record<LanguageCode, string>;
}

interface AdminOffCat {
  id: number;
  volgorde: number;
  categorie_nl: string;
  categorie_fr: string;
  categorie_en: string;
}

interface AdminWebCat {
  id: number;
  volgorde: number;
  io_type: string;
  typeIcon: string;
  categorie_nl: string;
  categorie_fr: string;
  categorie_en: string;
}

function parseNum(value: number | string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const tableContainerSx = { maxHeight: "65vh" };

const createItemWithOffCat = createItem as (
  type: "ingangen" | "uitgangen",
  nodeid: string,
  parent: string,
  typeIcon: string,
  moduleType: string,
  webCat: number,
  offCat: number,
  labels: Record<string, string>
) => Promise<unknown>;

const updateItemWithOffCat = updateItem as (
  type: "ingangen" | "uitgangen",
  id: number,
  nodeid: string,
  typeIcon: string,
  moduleType: string,
  webCat: number,
  offCat: number
) => Promise<unknown>;

function AdminConfigurator() {
  const [modules, setModules] = useState<AdminModule[]>([]);
  const [ingangen, setIngangen] = useState<AdminItem[]>([]);
  const [uitgangen, setUitgangen] = useState<AdminItem[]>([]);
  const [webcats, setWebcats] = useState<AdminWebCat[]>([]);
  const [offcats, setOffcats] = useState<AdminOffCat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [savingKey, setSavingKey] = useState<string>("");
  const [activeSection, setActiveSection] = useState<AdminSection>("modules");
  const [hideObsolete, setHideObsolete] = useState<boolean>(true);

  const emptyNewItem = () => ({
    nodeid: "",
    parent: "0",
    typeIcon: "",
    moduleType: "",
    webCat: 0,
    offCat: 0,
    labels: { NL: "", FR: "", EN: "" } as Record<LanguageCode, string>,
  });

  const [newIngangen, setNewIngangen] = useState(emptyNewItem);
  const [newUitgangen, setNewUitgangen] = useState(emptyNewItem);
  const [creating, setCreating] = useState<string>("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [modulesRes, webcatsRes, offcatsRes,
        ingangenNL, ingangenFR, ingangenEN,
        uitgangenNL, uitgangenFR, uitgangenEN,
      ] = await Promise.all([
        getModulesFromDatabase(),
        getWebCatsFromDatabase(),
        getOffCatsFromDatabase(),
        GetOutputsFromDatabase("ingangen", "NL"),
        GetOutputsFromDatabase("ingangen", "FR"),
        GetOutputsFromDatabase("ingangen", "EN"),
        GetOutputsFromDatabase("uitgangen", "NL"),
        GetOutputsFromDatabase("uitgangen", "FR"),
        GetOutputsFromDatabase("uitgangen", "EN"),
      ]);

      const mappedModules: AdminModule[] = (modulesRes?.success || []).map((mod: any) => ({
        id: mod.id,
        type: mod.type,
        name: mod.name || mod.type,
        busConsumption: parseNum(mod.busConsumption),
        outputs: parseNum(mod.outputs),
        adresses: parseNum(mod.adresses),
        prijs: parseNum(mod.prijs),
        obsolete: parseNum(mod.obsolete),
        webCat: parseNum(mod.webCat),
        offCat: parseNum(mod.offCat),
      }));

      const buildLabelMap = (res: any): Record<number, string> => {
        const map: Record<number, string> = {};
        (res?.success || []).forEach((item: any) => { map[item.id] = item.label || ""; });
        return map;
      };

      const mapItems = (
        baseRes: any,
        nlMap: Record<number, string>,
        frMap: Record<number, string>,
        enMap: Record<number, string>,
      ): AdminItem[] =>
        (baseRes?.success || []).map((item: any): AdminItem => ({
          id: item.id,
          nodeid: item.nodeid,
          type: item.type,
          typeIcon: item.typeIcon || "",
          moduleType: item.moduleType || "",
          webCat: parseNum(item.webCat),
          offCat: parseNum(item.offCat),
          label: nlMap[item.id] || "",
          labels: {
            NL: nlMap[item.id] || "",
            FR: frMap[item.id] || "",
            EN: enMap[item.id] || "",
          },
        }));

      const mappedIngangen = mapItems(ingangenNL, buildLabelMap(ingangenNL), buildLabelMap(ingangenFR), buildLabelMap(ingangenEN));
      const mappedUitgangen = mapItems(uitgangenNL, buildLabelMap(uitgangenNL), buildLabelMap(uitgangenFR), buildLabelMap(uitgangenEN));
      const mappedWebcats = (webcatsRes?.success || []).map((wc: any) => ({
        id: wc.id,
        volgorde: parseNum(wc.volgorde),
        io_type: wc.io_type || "",
        typeIcon: wc.typeIcon || "",
        categorie_nl: wc.categorie_nl || "",
        categorie_fr: wc.categorie_fr || "",
        categorie_en: wc.categorie_en || "",
      }));

      const mappedOffcats = (offcatsRes?.success || []).map((oc: any) => ({
        id: oc.id,
        volgorde: parseNum(oc.volgorde),
        categorie_nl: oc.categorie_nl || "",
        categorie_fr: oc.categorie_fr || "",
        categorie_en: oc.categorie_en || "",
      }));

      setModules(mappedModules);
      setIngangen(mappedIngangen);
      setUitgangen(mappedUitgangen);
      setWebcats(mappedWebcats);
      setOffcats(mappedOffcats);
    } catch (loadErr) {
      setError("Failed to load admin configuration data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const moduleTypeOptions = useMemo(() => {
    return Array.from(new Set(modules.map((mod) => mod.type))).sort();
  }, [modules]);

  const obsoleteTypes = useMemo(() => {
    return new Set(modules.filter((mod) => mod.obsolete === 1).map((mod) => mod.type));
  }, [modules]);

  const webcatsIngangen = useMemo(() => webcats.filter((wc) => wc.io_type === "Ingangen"), [webcats]);
  const webcatsUitgangen = useMemo(() => webcats.filter((wc) => wc.io_type === "Uitgangen"), [webcats]);

  const updateModuleField = useCallback((id: number, field: keyof AdminModule, value: string) => {
    setModules((prev) =>
      prev.map((mod) =>
        mod.id === id
          ? {
              ...mod,
              [field]: field === "type" ? value : parseNum(value),
            }
          : mod
      )
    );
  }, []);

  const updateItemField = useCallback((type: IoType, id: number, field: keyof AdminItem, value: string) => {
    const setter = type === "ingangen" ? setIngangen : setUitgangen;
    setter((prev) =>
      prev.map((item) => {
        if (item.id !== id) {
          return item;
        }

        if (field === "webCat") {
          return { ...item, webCat: parseNum(value) };
        }

        if (field === "offCat") {
          return { ...item, offCat: parseNum(value) };
        }

        return { ...item, [field]: value };
      })
    );
  }, []);

  const updateItemLabelField = useCallback((type: IoType, id: number, language: LanguageCode, value: string) => {
    const setter = type === "ingangen" ? setIngangen : setUitgangen;

    setter((prev) =>
      prev.map((item) => {
        if (item.id !== id) {
          return item;
        }

        return {
          ...item,
          labels: {
            ...item.labels,
            [language]: value,
          },
        };
      })
    );
  }, []);

  const updateWebCatField = useCallback((id: number, field: keyof AdminWebCat, value: string) => {
    setWebcats((prev) =>
      prev.map((wc) => {
        if (wc.id !== id) {
          return wc;
        }

        if (field === "volgorde") {
          return { ...wc, volgorde: parseNum(value) };
        }

        return { ...wc, [field]: value };
      })
    );
  }, []);

  const handleCreate = useCallback(async (type: IoType) => {
    const form = type === "ingangen" ? newIngangen : newUitgangen;
    const setter = type === "ingangen" ? setNewIngangen : setNewUitgangen;

    if (!form.nodeid.trim()) {
      setError("Node ID is required.");
      return;
    }

    setCreating(type);
    setError("");

    try {
      await createItemWithOffCat(
        type,
        form.nodeid.trim(),
        form.parent.trim(),
        form.typeIcon.trim(),
        form.moduleType,
        parseNum(form.webCat),
        parseNum(form.offCat),
        form.labels
      );
      setter(emptyNewItem());
      await loadAll();
    } catch (err) {
      setError(`Failed to create ${type} item.`);
    } finally {
      setCreating("");
    }
  }, [newIngangen, newUitgangen, loadAll]);

  const saveModule = useCallback(async (module: AdminModule) => {
    const key = `module-${module.id}`;
    setSavingKey(key);
    setError("");

    try {
      await UpdateModuleAdmin({
        id: module.id,
        busConsumption: parseNum(module.busConsumption),
        adresses: parseNum(module.adresses),
        prijs: parseNum(module.prijs),
        obsolete: parseNum(module.obsolete),
        offCat: parseNum(module.offCat),
        webCat: parseNum(module.webCat),
      });
      await loadAll();
    } catch (saveErr) {
      setError(`Failed to save module ${module.type} (${module.id}).`);
    } finally {
      setSavingKey("");
    }
  }, [loadAll]);

  const saveItem = useCallback(async (type: IoType, item: AdminItem) => {
    const key = `${type}-${item.id}`;
    setSavingKey(key);
    setError("");

    try {
      await updateItemWithOffCat(
        type,
        item.id,
        item.nodeid,
        item.typeIcon,
        item.moduleType,
        parseNum(item.webCat),
        parseNum(item.offCat)
      );

      const translationsToSave = (Object.keys(item.labels) as LanguageCode[])
        .map((language) => ({ language, label: (item.labels[language] || "").trim() }))
        .filter((translation) => translation.label.length > 0);

      if (translationsToSave.length) {
        await Promise.all(
          translationsToSave.map((translation) =>
            updateTranslation(type, item.id, translation.language, translation.label)
          )
        );
      }
    } catch (saveErr) {
      setError(`Failed to save ${type} item ${item.id}.`);
    } finally {
      setSavingKey("");
    }
  }, []);

  const updateOffCatField = useCallback((id: number, field: keyof AdminOffCat, value: string) => {
    setOffcats((prev) =>
      prev.map((oc) => {
        if (oc.id !== id) return oc;
        if (field === "volgorde") return { ...oc, volgorde: parseNum(value) };
        return { ...oc, [field]: value };
      })
    );
  }, []);

  const saveOffCat = useCallback(async (offcat: AdminOffCat) => {
    const key = `offcat-${offcat.id}`;
    setSavingKey(key);
    setError("");
    try {
      await updateOffCat(offcat);
    } catch (err) {
      setError(`Failed to save offcat ${offcat.id}.`);
    } finally {
      setSavingKey("");
    }
  }, []);

  const saveWebCat = useCallback(async (webcat: AdminWebCat) => {
    const key = `webcat-${webcat.id}`;
    setSavingKey(key);
    setError("");

    try {
      await updateWebCat(webcat);
    } catch (saveErr) {
      setError(`Failed to save webcat ${webcat.id}.`);
    } finally {
      setSavingKey("");
    }
  }, []);

  if (loading) {
    return <div>Loading admin configurator...</div>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" onClick={loadAll}>
          Refresh all
        </Button>
        {error && <div>{error}</div>}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button
          variant={activeSection === "modules" ? "contained" : "outlined"}
          onClick={() => setActiveSection("modules")}
        >
          Modules
        </Button>
        <Button
          variant={activeSection === "ingangen" ? "contained" : "outlined"}
          onClick={() => setActiveSection("ingangen")}
        >
          Ingangen
        </Button>
        <Button
          variant={activeSection === "uitgangen" ? "contained" : "outlined"}
          onClick={() => setActiveSection("uitgangen")}
        >
          Uitgangen
        </Button>
        <Button
          variant={activeSection === "webcats" ? "contained" : "outlined"}
          onClick={() => setActiveSection("webcats")}
        >
          Webcats
        </Button>
          <Button
            variant={activeSection === "offcats" ? "contained" : "outlined"}
            onClick={() => setActiveSection("offcats")}
          >
            Offcats
          </Button>
      </Stack>

      {activeSection === "modules" && (
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <h3 style={{ margin: 0 }}>Modules</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={hideObsolete}
              onChange={(e) => setHideObsolete(e.target.checked)}
            />
            Hide obsolete
          </label>
        </Stack>
        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Naam</TableCell>
                <TableCell>BusConsumption</TableCell>
                <TableCell>Outputs</TableCell>
                <TableCell>Adresses</TableCell>
                <TableCell>Prijs</TableCell>
                {!hideObsolete && <TableCell>Obsolete</TableCell>}
                <TableCell>Webcat</TableCell>
                  <TableCell>Offcat</TableCell>
                  <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modules
                .filter((mod) => !hideObsolete || mod.obsolete !== 1)
                .map((mod) => {
                const key = `module-${mod.id}`;
                return (
                  <TableRow key={mod.id}>
                    <TableCell>{mod.id}</TableCell>
                    <TableCell>{mod.name}</TableCell>
                    <TableCell>
                      <input
                        type="number"
                        style={{ width: 60 }}
                        value={mod.busConsumption}
                        onChange={(e) => updateModuleField(mod.id, "busConsumption", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        style={{ width: 60 }}
                        value={mod.outputs}
                        onChange={(e) => updateModuleField(mod.id, "outputs", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        style={{ width: 60 }}
                        value={mod.adresses}
                        onChange={(e) => updateModuleField(mod.id, "adresses", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        step="0.01"
                        style={{ width: 70 }}
                        value={mod.prijs}
                        onChange={(e) => updateModuleField(mod.id, "prijs", e.target.value)}
                      />
                    </TableCell>
                    {!hideObsolete && (
                    <TableCell>
                      <select
                        value={mod.obsolete}
                        onChange={(e) => updateModuleField(mod.id, "obsolete", e.target.value)}
                      >
                        <option value={0}>No</option>
                        <option value={1}>Yes</option>
                      </select>
                    </TableCell>
                    )}
                    <TableCell>
                      <select
                        value={mod.webCat}
                        onChange={(e) => updateModuleField(mod.id, "webCat", e.target.value)}
                      >
                        <option value={0}>-</option>
                        {webcats.map((wc) => (
                          <option key={wc.id} value={wc.id}>
                            {wc.categorie_nl}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                        <select
                          value={mod.offCat}
                          onChange={(e) => updateModuleField(mod.id, "offCat", e.target.value)}
                        >
                          <option value={0}>-</option>
                          {offcats.map((oc) => (
                            <option key={oc.id} value={oc.id}>
                              {oc.categorie_nl}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <Button variant="contained" size="small" disabled={savingKey === key} onClick={() => saveModule(mod)}>
                          {savingKey === key ? "Saving..." : "Save"}
                        </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {activeSection === "ingangen" && (
      <Paper sx={{ p: 2 }}>
        <h3>Ingangen</h3>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <h4 style={{ margin: "0 0 8px 0" }}>Nieuwe ingang</h4>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <label>Node<br /><input type="text" placeholder="bv. 1.5" value={newIngangen.nodeid} onChange={(e) => setNewIngangen((p) => ({ ...p, nodeid: e.target.value }))} style={{ width: 70 }} /></label>
            <label>Icon<br />
              <select value={newIngangen.typeIcon} onChange={(e) => setNewIngangen((p) => ({ ...p, typeIcon: e.target.value }))}>
                <option value="">-</option>
                {["btn", "inp", "mot", "sen", "temp", "fan", "sun", "energy", "touch"].map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </label>
            <label>Module<br />
              <select value={newIngangen.moduleType} onChange={(e) => setNewIngangen((p) => ({ ...p, moduleType: e.target.value }))}>
                <option value="">-</option>
                {moduleTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Webcat<br />
              <select value={newIngangen.webCat} onChange={(e) => setNewIngangen((p) => ({ ...p, webCat: parseNum(e.target.value) }))}>
                <option value={0}>-</option>
                {webcatsIngangen.map((wc) => <option key={wc.id} value={wc.id}>{wc.categorie_nl}</option>)}
              </select>
            </label>
            <label>Offcat<br />
              <select value={newIngangen.offCat} onChange={(e) => setNewIngangen((p) => ({ ...p, offCat: parseNum(e.target.value) }))}>
                <option value={0}>-</option>
                {offcats.map((oc) => <option key={oc.id} value={oc.id}>{oc.categorie_nl}</option>)}
              </select>
            </label>
            <label>NL<br /><input type="text" value={newIngangen.labels.NL} onChange={(e) => setNewIngangen((p) => ({ ...p, labels: { ...p.labels, NL: e.target.value } }))} style={{ width: 120 }} /></label>
            <label>FR<br /><input type="text" value={newIngangen.labels.FR} onChange={(e) => setNewIngangen((p) => ({ ...p, labels: { ...p.labels, FR: e.target.value } }))} style={{ width: 120 }} /></label>
            <label>EN<br /><input type="text" value={newIngangen.labels.EN} onChange={(e) => setNewIngangen((p) => ({ ...p, labels: { ...p.labels, EN: e.target.value } }))} style={{ width: 120 }} /></label>
            <div style={{ alignSelf: "flex-end" }}>
              <Button variant="contained" size="small" disabled={creating === "ingangen"} onClick={() => handleCreate("ingangen")}>
                {creating === "ingangen" ? "Adding..." : "Add"}
              </Button>
            </div>
          </Stack>
        </Paper>
        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Node</TableCell>
                <TableCell>Icon</TableCell>
                <TableCell>Module link</TableCell>
                <TableCell>Webcat</TableCell>
                <TableCell>NL</TableCell>
                <TableCell>FR</TableCell>
                <TableCell>EN</TableCell>
                <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ingangen
                .filter((item) => item.webCat !== 0)
                .filter((item) => !hideObsolete || !item.moduleType || !obsoleteTypes.has(item.moduleType))
                .slice()
                .sort((a, b) => a.webCat - b.webCat || a.nodeid.localeCompare(b.nodeid, undefined, { numeric: true, sensitivity: "base" }))
                .map((item) => {
                const key = `ingangen-${item.id}`;
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <input type="text" value={item.nodeid} style={{ width: 60 }} onChange={(e) => updateItemField("ingangen", item.id, "nodeid", e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <select value={item.typeIcon} onChange={(e) => updateItemField("ingangen", item.id, "typeIcon", e.target.value)}>
                        <option value="">-</option>
                        {["btn", "inp", "mot", "sen", "temp", "fan", "sun", "energy", "touch"].map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select value={item.moduleType} onChange={(e) => updateItemField("ingangen", item.id, "moduleType", e.target.value)}>
                        <option value="">-</option>
                        {moduleTypeOptions.map((typeOption) => (
                          <option key={typeOption} value={typeOption}>
                            {typeOption}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select
                        value={item.webCat}
                        onChange={(e) => updateItemField("ingangen", item.id, "webCat", e.target.value)}
                      >
                        <option value={0}>-</option>
                        {webcatsIngangen.map((wc) => (
                          <option key={wc.id} value={wc.id}>
                            {wc.categorie_nl}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.labels.NL}
                        onChange={(e) => updateItemLabelField("ingangen", item.id, "NL", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.labels.FR}
                        onChange={(e) => updateItemLabelField("ingangen", item.id, "FR", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.labels.EN}
                        onChange={(e) => updateItemLabelField("ingangen", item.id, "EN", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="contained" size="small" disabled={savingKey === key} onClick={() => saveItem("ingangen", item)}>
                        {savingKey === key ? "Saving..." : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {activeSection === "uitgangen" && (
      <Paper sx={{ p: 2 }}>
        <h3>Uitgangen</h3>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <h4 style={{ margin: "0 0 8px 0" }}>Nieuwe uitgang</h4>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <label>Node<br /><input type="text" placeholder="bv. 1.5" value={newUitgangen.nodeid} onChange={(e) => setNewUitgangen((p) => ({ ...p, nodeid: e.target.value }))} style={{ width: 70 }} /></label>
            <label>Icon<br />
              <select value={newUitgangen.typeIcon} onChange={(e) => setNewUitgangen((p) => ({ ...p, typeIcon: e.target.value }))}>
                <option value="">-</option>
                {["dimmer", "relais", "sen", "rol", "rol2"].map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </label>
            <label>Module<br />
              <select value={newUitgangen.moduleType} onChange={(e) => setNewUitgangen((p) => ({ ...p, moduleType: e.target.value }))}>
                <option value="">-</option>
                {moduleTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Webcat<br />
              <select value={newUitgangen.webCat} onChange={(e) => setNewUitgangen((p) => ({ ...p, webCat: parseNum(e.target.value) }))}>
                <option value={0}>-</option>
                {webcatsUitgangen.map((wc) => <option key={wc.id} value={wc.id}>{wc.categorie_nl}</option>)}
              </select>
            </label>
            <label>Offcat<br />
              <select value={newUitgangen.offCat} onChange={(e) => setNewUitgangen((p) => ({ ...p, offCat: parseNum(e.target.value) }))}>
                <option value={0}>-</option>
                {offcats.map((oc) => <option key={oc.id} value={oc.id}>{oc.categorie_nl}</option>)}
              </select>
            </label>
            <label>NL<br /><input type="text" value={newUitgangen.labels.NL} onChange={(e) => setNewUitgangen((p) => ({ ...p, labels: { ...p.labels, NL: e.target.value } }))} style={{ width: 120 }} /></label>
            <label>FR<br /><input type="text" value={newUitgangen.labels.FR} onChange={(e) => setNewUitgangen((p) => ({ ...p, labels: { ...p.labels, FR: e.target.value } }))} style={{ width: 120 }} /></label>
            <label>EN<br /><input type="text" value={newUitgangen.labels.EN} onChange={(e) => setNewUitgangen((p) => ({ ...p, labels: { ...p.labels, EN: e.target.value } }))} style={{ width: 120 }} /></label>
            <div style={{ alignSelf: "flex-end" }}>
              <Button variant="contained" size="small" disabled={creating === "uitgangen"} onClick={() => handleCreate("uitgangen")}>
                {creating === "uitgangen" ? "Adding..." : "Add"}
              </Button>
            </div>
          </Stack>
        </Paper>
        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Node</TableCell>
                <TableCell>Icon</TableCell>
                <TableCell>Module link</TableCell>
                <TableCell>Webcat</TableCell>
                <TableCell>NL</TableCell>
                <TableCell>FR</TableCell>
                <TableCell>EN</TableCell>
                <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uitgangen
                .filter((item) => item.webCat !== 0)
                .filter((item) => !hideObsolete || !item.moduleType || !obsoleteTypes.has(item.moduleType))
                .slice()
                .sort((a, b) => a.webCat - b.webCat || a.nodeid.localeCompare(b.nodeid, undefined, { numeric: true, sensitivity: "base" }))
                .map((item) => {
                const key = `uitgangen-${item.id}`;
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <input type="text" value={item.nodeid} style={{ width: 60 }} onChange={(e) => updateItemField("uitgangen", item.id, "nodeid", e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <select value={item.typeIcon} onChange={(e) => updateItemField("uitgangen", item.id, "typeIcon", e.target.value)}>
                        <option value="">-</option>
                        {["dimmer", "relais", "sen", "rol", "rol2"].map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select value={item.moduleType} onChange={(e) => updateItemField("uitgangen", item.id, "moduleType", e.target.value)}>
                        <option value="">-</option>
                        {moduleTypeOptions.map((typeOption) => (
                          <option key={typeOption} value={typeOption}>
                            {typeOption}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select
                        value={item.webCat}
                        onChange={(e) => updateItemField("uitgangen", item.id, "webCat", e.target.value)}
                      >
                        <option value={0}>-</option>
                        {webcatsUitgangen.map((wc) => (
                          <option key={wc.id} value={wc.id}>
                            {wc.categorie_nl}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.labels.NL}
                        onChange={(e) => updateItemLabelField("uitgangen", item.id, "NL", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.labels.FR}
                        onChange={(e) => updateItemLabelField("uitgangen", item.id, "FR", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.labels.EN}
                        onChange={(e) => updateItemLabelField("uitgangen", item.id, "EN", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="contained" size="small" disabled={savingKey === key} onClick={() => saveItem("uitgangen", item)}>
                        {savingKey === key ? "Saving..." : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {activeSection === "webcats" && (
      <Paper sx={{ p: 2 }}>
        <h3>Webcats</h3>
        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Volgorde</TableCell>
                <TableCell>IO Type</TableCell>
                <TableCell>Icon</TableCell>
                <TableCell>NL</TableCell>
                <TableCell>FR</TableCell>
                <TableCell>EN</TableCell>
                <TableCell>Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webcats.map((wc) => {
                const key = `webcat-${wc.id}`;
                return (
                  <TableRow key={wc.id}>
                    <TableCell>{wc.id}</TableCell>
                    <TableCell>
                      <input
                        type="number"
                        value={wc.volgorde}
                        onChange={(e) => updateWebCatField(wc.id, "volgorde", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={wc.io_type}
                        onChange={(e) => updateWebCatField(wc.id, "io_type", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={wc.typeIcon}
                        onChange={(e) => updateWebCatField(wc.id, "typeIcon", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={wc.categorie_nl}
                        onChange={(e) => updateWebCatField(wc.id, "categorie_nl", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={wc.categorie_fr}
                        onChange={(e) => updateWebCatField(wc.id, "categorie_fr", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={wc.categorie_en}
                        onChange={(e) => updateWebCatField(wc.id, "categorie_en", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="contained" size="small" disabled={savingKey === key} onClick={() => saveWebCat(wc)}>
                        {savingKey === key ? "Saving..." : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}
      {activeSection === "offcats" && (
        <Paper sx={{ p: 2 }}>
          <h3>Offcats</h3>
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Volgorde</TableCell>
                  <TableCell>NL</TableCell>
                  <TableCell>FR</TableCell>
                  <TableCell>EN</TableCell>
                  <TableCell>Save</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offcats.map((oc) => {
                  const key = `offcat-${oc.id}`;
                  return (
                    <TableRow key={oc.id}>
                      <TableCell>{oc.id}</TableCell>
                      <TableCell>
                        <input type="number" style={{ width: 60 }} value={oc.volgorde} onChange={(e) => updateOffCatField(oc.id, "volgorde", e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <input type="text" value={oc.categorie_nl} onChange={(e) => updateOffCatField(oc.id, "categorie_nl", e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <input type="text" value={oc.categorie_fr} onChange={(e) => updateOffCatField(oc.id, "categorie_fr", e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <input type="text" value={oc.categorie_en} onChange={(e) => updateOffCatField(oc.id, "categorie_en", e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <Button variant="contained" size="small" disabled={savingKey === key} onClick={() => saveOffCat(oc)}>
                          {savingKey === key ? "Saving..." : "Save"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        )}
    </Stack>
  );
}

export default AdminConfigurator;
